import { useState } from 'react';
import { useIntl } from 'react-intl';
import { AutomationTarget, Queue } from '@rossum/api-client/queues';
import { DetailDrawer } from '@rossum/ui';
import { Button, CircularProgress } from '@rossum/ui/material';
import { SchemaWithEtag } from '../../../business/schema/useSchema';
import { SchemaFieldWithPathNonNullableField } from '../../schemas/getFlattenSchemaFields';
import { OptimizationAssistant } from './automation-assistant/OptimizationAssistant';
import { useAutomationFlags } from './core/hooks/useAutomationFlags';
import { LegacyAutomationSettings } from './legacy-automation/LegacyAutomationSettings';

type ConfigureAutomationProps = {
  schemaMap: Record<string, SchemaFieldWithPathNonNullableField>;
  queueSchema: SchemaWithEtag | undefined;
  savedAutomationTargets: AutomationTarget[] | undefined;
  queue: Queue;
  isLoading: boolean;
  isAuroraQueue: boolean;
};

export const ConfigureAutomation = ({
  schemaMap,
  queueSchema,
  savedAutomationTargets,
  queue,
  isLoading,
  isAuroraQueue,
}: ConfigureAutomationProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const { queueLevelFlags, isAutomationAssistantPurchased } =
    useAutomationFlags();

  const isUsingAssistant = queueLevelFlags.isUsingAssistant(queue);

  // null means "follow the queue's own setting". It is only set when the user
  // navigates within the drawer, ie. opts into the assistant from the banner in
  // the standard settings, before the queue itself is using it.
  const [assistantViewOverride, setAssistantViewOverride] = useState<
    boolean | null
  >(null);

  const isAssistantActive = assistantViewOverride ?? isUsingAssistant;

  const handleClose = () => {
    setOpen(false);
    setAssistantViewOverride(null);
  };

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        onClick={() => {
          setOpen(!open);
        }}
        startIcon={isLoading ? <CircularProgress size={16} /> : null}
        disabled={isLoading}
      >
        {intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimizeButton',
        })}
      </Button>

      <DetailDrawer
        open={open}
        title={
          isAssistantActive
            ? intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.automationAssistant.drawerTitle',
              })
            : intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.standardAutomation.drawerTitle',
              })
        }
        onClose={handleClose}
      >
        {isAssistantActive ? (
          <OptimizationAssistant
            onClose={handleClose}
            onSwitchBack={() => setAssistantViewOverride(false)}
            readOnly={!isAutomationAssistantPurchased}
            queue={queue}
            schemaMap={schemaMap}
            queueSchema={queueSchema}
            savedAutomationTargets={savedAutomationTargets}
          />
        ) : (
          <LegacyAutomationSettings
            queueSchema={queueSchema}
            queue={queue}
            onTryAutomationAssistant={() => setAssistantViewOverride(true)}
            isAuroraQueue={isAuroraQueue}
          />
        )}
      </DetailDrawer>
    </>
  );
};
